// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import {
  type EntityAttachmentId,
  type EntityAttachmentType,
  type UploadableEntityAttachmentType,
  type FileId,
  isUploadableEntityAttachmentType,
} from '@evolvemep/api-ts';

import { FileUploads } from 'components/Mantine/FileUploads/FileUploads';
import { useAddWebLinks } from 'components/Mantine/FileUploads/useAddWebLinks';
import { useUploadFiles } from 'components/Mantine/FileUploads/useUploadFiles';

type SectionFileUploadsProps<T extends EntityAttachmentType> = {
  entityType: T;
  entityId: EntityAttachmentId<T>;
  label: string;
  description?: string;
  showUploadBox?: boolean;
  disabled?: boolean;
  /** Called after files have been successfully uploaded. */
  onFilesUploaded?: (fileIds: FileId[]) => void;
  /** Called after a web link has been successfully added. */
  onWebLinkAdded?: () => void;
  children: ReactNode;
};

const UploadableFileUploads = <T extends UploadableEntityAttachmentType>({
  entityType,
  entityId,
  onFilesUploaded,
  onWebLinkAdded,
  ...fileUploadsProps
}: SectionFileUploadsProps<T>) => {
  const { uploadFiles, saving: uploadingFiles } = useUploadFiles(entityType, entityId);
  const { addWebLink, saving: addingWebLink } = useAddWebLinks(entityType, entityId);

  return (
    <FileUploads
      {...fileUploadsProps}
      onAddFiles={async (files) => uploadFiles(files).then(onFilesUploaded)}
      onAddWebLink={async (webLink) => addWebLink(webLink).then(onWebLinkAdded)}
      loading={uploadingFiles || addingWebLink}
      flex="0 1 auto"
    />
  );
};

// Narrowing the whole props object keeps `entityType` and `entityId` paired — TS cannot narrow
// `EntityAttachmentId<T>` from a guard on `entityType` alone
const isUploadableSectionProps = <T extends EntityAttachmentType>(
  props: SectionFileUploadsProps<T>,
): props is SectionFileUploadsProps<T & UploadableEntityAttachmentType> =>
  isUploadableEntityAttachmentType(props.entityType);

/**
 * Renders an attachment section's `FileUploads` wrapper.
 * Roll-up sections are read-only, so they render without upload handlers.
 */
export const SectionFileUploads = <T extends EntityAttachmentType>(props: SectionFileUploadsProps<T>) => {
  if (isUploadableSectionProps(props)) {
    return <UploadableFileUploads {...props} />;
  }
  const {
    entityType: _entityType,
    entityId: _entityId,
    onFilesUploaded: _onFilesUpload,
    onWebLinkAdded: _onWebLinkAdded,
    ...fileUploadsProps
  } = props;
  return <FileUploads {...fileUploadsProps} flex="0 1 auto" />;
};
